/**
 * Publishes a list of items unlocked using access codes.
 * @namespace Unlocks
 * @augments Publisher
 */

/**
 * The base unlock schema.
 * @typedef {object} Unlock
 * @property {string} accessCode - The access code that was applied.
 */

/**
 * The schema for an event-level unlock.
 * @typedef {Unlock} EventUnlock
 * @property {number} eventId - The event that the access code was applied to.
 * @property {object} filterConfiguration - Pass-through value from the server response.
 * @property {object[]} holds - Pass-through value from the server response.
 * @property {object[]} saleLocks - Pass-through value from the server response.
 */

/**
 * The digest being published, containing everything that's been unlocked via access code.
 * @typedef {object} Unlocks
 * @property {EventUnlock[]} eventUnlocks - The list of everything the fan has unlocked via access code.
 */

inherit("Unlocks", {
	parent: `Publisher`,
	requires: [`XHR`],
	singleton: true,
	prepare(setup) {
		this.setInitialData();
	},
	consructor(setup) {
		this.reconcile();
	},

	api: {
		setInitialData: {
			value() {
				const { data } = this;
				const responses = []; // the complete list of what's been unlocked

				this.assign(data, { responses });
			},
		},

		tryAccessCode: {
			async: true,
			public: true,
			async value(context, options) {
				const { accessCode, scope } = options;
				const { XHR, data } = this;
				const { responses } = data;
				const response = { accessCode, scope };

				switch (scope) {
					case `event`: {
						await this.require({ deps: [`ShoppingCart`] });
						const { ShoppingCart } = this;
						const cart = await ShoppingCart.get();
						const { groupSaleToken } = cart;
						const urlSuffix = this.isString(groupSaleToken, 1) ? `?gst=${groupSaleToken}` : ``;
						const { eventId } = options;

						const body = await XHR.ajax({
							type: `GET`,
							url: `/api/events/${eventId}/locks/${accessCode}${urlSuffix}`,
						});

						// remove any existing entries for this combo
						this.extract(responses, { accessCode, eventId, scope });

						// add values to the response entry
						this.assign(response, { body, eventId });
						break;
					}
					default: {
						throw new Error(`tryAccessCode(): Invalid scope "${scope}" provided.`);
					}
				}

				// add the new response to the list
				responses.push(response);

				// reconcile the responses to update the digest
				this.reconcile();
			},
		},

		reconcile: {
			value() {
				const { data } = this;
				const { responses } = data;
				const eventUnlocks = [];

				// iterate through the latest set of server responses
				this.each(responses, (response) => {
					const { accessCode, scope } = response;

					if (scope === `event`) {
						const { body, eventId } = response;
						const { filterConfiguration, holds, saleLocks } = body;
						eventUnlocks.push({ accessCode, eventId, filterConfiguration, holds, saleLocks });
					}
				});

				// publish the generated list
				this.update({ eventUnlocks });
			},
		},
	},
});

//# sourceMappingURL=Unlocks.js.map